import {createSlice, PayloadAction} from '@reduxjs/toolkit';

/**
 * Pages that preserve their expanded grid groups across a trip to a form or sub-page.
 *
 * Each key owns one snapshot, so leaving the timesheet does not disturb a snapshot the
 * cost report armed earlier.
 */
export type GridViewKey = 'timesheet' | 'timereport' | 'costreport';

export interface GridViewSnapshot {
    /**
     * Page-specific discriminator — for the timesheet, the grouping mode. Group keys only mean
     * something within one scope, so a snapshot taken in another scope is discarded on the way
     * back rather than opening unrelated groups.
     */
    scope: string;
    expandedGroupKeys: Array<string>;
}

interface State {
    snapshots: Partial<Record<GridViewKey, GridViewSnapshot>>;
}

const initialState: State = {
    snapshots: {}
};

const gridViewHandoffSlice = createSlice({
    name: 'gridViewHandoff',
    initialState,
    reducers: {
        armViewSnapshot: (state, action: PayloadAction<{ view: GridViewKey } & GridViewSnapshot>) => {
            const { view, scope, expandedGroupKeys } = action.payload;
            state.snapshots[view] = {
                scope,
                // Group keys are display values; '' never identifies a real group (the grid skips it).
                expandedGroupKeys: expandedGroupKeys.filter((key) => key !== '')
            };
        },
        consumeViewSnapshot: (state, action: PayloadAction<GridViewKey>) => {
            delete state.snapshots[action.payload];
        }
    }
});

export const { armViewSnapshot, consumeViewSnapshot } = gridViewHandoffSlice.actions;

export default gridViewHandoffSlice.reducer;
